import { motion } from 'framer-motion';
import { CreditCard, LayoutGrid, Siren, Timer, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getScaledUrl } from '@/lib/cloudinary';

const COMMAND_CENTER_IMAGE =
  'https://res.cloudinary.com/dnukl7bmh/image/upload/v1770735535/3023727_wusze7.png';
const DUGOUT_INTEL_IMAGE =
  'https://res.cloudinary.com/dnukl7bmh/image/upload/v1770735535/3024223_wltzon.png';
const FAN_EXPERIENCE_IMAGE =
  'https://res.cloudinary.com/dnukl7bmh/image/upload/v1770735561/holo-generated-1770733692184_r3uycy.png';

const phases = [
  {
    id: 'build',
    label: 'Phase 1: The Build',
    cards: [
      {
        title: 'Drag & Drop Scheduling with Conflict Engine',
        description:
          'Place games on the grid. Overlaps, diamond clashes, and rest rules stop the drop.',
        icon: LayoutGrid,
        imageUrl: COMMAND_CENTER_IMAGE,
        imageAlt: 'Command Center interface with drag-and-drop scheduling',
      },
      {
        title: 'Universal Registration Suite',
        description:
          'One intake for teams, payments, and waivers before the first pitch is thrown.',
        icon: CreditCard,
      },
    ],
  },
  {
    id: 'gameday',
    label: 'Phase 2: Game Day Autopilot',
    cards: [
      {
        title: 'Coach Score Input',
        description:
          'A coach posts the final score from the dugout. Standings update from that result.',
        icon: Timer,
        imageUrl: FAN_EXPERIENCE_IMAGE,
        imageAlt: 'Coach posting a final score from the dugout',
      },
      {
        title: 'Automated Tie-Breakers',
        description:
          'Pool standings and seeds update from the same ladder the coaches already trust.',
        icon: Trophy,
        imageUrl: DUGOUT_INTEL_IMAGE,
        imageAlt: 'Live bracket and standings',
      },
    ],
  },
] as const;

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4 },
  }),
};

export function BentoFeatureGrid() {
  return (
    <section className="bg-[#020617] py-16 md:py-24" data-testid="bento-features">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 text-center font-broadcast text-3xl uppercase tracking-wide text-white md:mb-16 md:text-5xl"
        >
          The tournament lifecycle
        </motion.h2>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {phases.map(phase => (
            <div key={phase.id} className="flex flex-col gap-4">
              <p className="font-broadcast text-xs uppercase tracking-[0.22em] text-championship-gold">
                {phase.label}
              </p>
              {phase.cards.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    custom={index}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-30px' }}
                    className="flex-1"
                  >
                    <Card className="h-full overflow-hidden border-white/10 bg-white/5">
                      {'imageUrl' in feature && feature.imageUrl ? (
                        <div className="flex h-44 items-center justify-center overflow-hidden border-b border-white/10 bg-[#020617] md:h-52">
                          <img
                            src={getScaledUrl(feature.imageUrl, 640)}
                            alt={feature.imageAlt}
                            className="h-full w-full object-contain"
                            loading="lazy"
                            decoding="async"
                            width={640}
                            height={176}
                          />
                        </div>
                      ) : null}
                      <CardHeader className="pb-2">
                        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-500/15">
                          <Icon className="h-5 w-5 text-emerald-300" aria-hidden />
                        </div>
                        <CardTitle className="text-base text-white md:text-lg">
                          {feature.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-white/65">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="h-full"
          >
            <Card className="flex h-full flex-col border-red-500/40 bg-red-950/40">
              <CardHeader>
                <p className="font-broadcast text-xs uppercase tracking-[0.22em] text-red-300">
                  Phase 3: The Crisis Override
                </p>
                <div className="mb-1 mt-3 flex h-11 w-11 items-center justify-center rounded-lg bg-red-500/20">
                  <Siren className="h-5 w-5 text-red-300" aria-hidden />
                </div>
                <CardTitle className="text-xl text-white md:text-2xl">
                  The “Red Button” Emergency Suite
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col justify-end">
                <p className="text-sm leading-relaxed text-white/80 md:text-base">
                  Close a diamond, swap the field, and send one consolidated broadcast to coaches,
                  parents, and staff. The weekend keeps moving.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
